/**
 * ground_sampling — the seed-dependent inputs of the ground rules
 * (lib/ground_rules.ts): water classes, a river's inner bends, the signed
 * across-track distance of a regional route, the terrace (strata) band
 * weight, and the one function that turns a chunk's heights + climate
 * weights + masks into its interleaved ground buffer. Worker-safe (no three,
 * no DOM) — the chunk worker, the sync chunk path and the harness share it.
 *
 * Read-only against the oracles: `shared/terrain.ts` (and its mirrors) is
 * never touched — the river table is READ through `riverNetworkTable` with a
 * documented copy of its column layout (verify:ground-rules pins it against
 * `riverWaterAt`).
 */
import { BIOMES, landformHeight, terrainHeight, biomeWeights, terraceHeight, riverWaterAt, riverNetworkTable, type Biome } from '../../../shared/terrain.js';
import { routeSegments, type RouteSeg } from './regional_routes.js';
import { bakedLakesNear, type BakedLake } from './lakes.js';
import { forestDensityMap } from './map_layers.js';
import { GROUND_STRIDE, ROUTE_NONE, GROUND_LAYER_COUNT } from './ground_layers.js';
import {
  gridFor, buildRuleField, sampleRuleField, groundRule, emptyRuleInput, ruleNoise,
  FINE_STEP, FINE_MARGIN, COARSE_STEP, COARSE_MARGIN, FOREST_STEP, FOREST_MARGIN,
  WATER_NONE, WATER_SEA, WATER_RIVER, WATER_LAKE, type GridSpec, type RuleField,
} from './ground_rules.js';

/** A batched field filler (the AssemblyScript kernel's fillChunk) — optional; JS per point otherwise. */
export interface FieldFiller {
  fillChunk(seed: string, worldCX: number, worldCZ: number, size: number, seg: number): { heights: Float64Array; weights: Float64Array } | null;
}

// ── water ──────────────────────────────────────────────────────────────────
/** The water class at a point with terrain height `h`: sea below the sea level, a river channel, a baked lake below its level. */
export function waterClassAt(seed: string, sea: number, lakes: readonly BakedLake[], x: number, z: number, h: number): number {
  if (h < sea) return WATER_SEA;
  if (riverWaterAt(seed, x, z) !== null) return WATER_RIVER;
  for (const l of lakes) if (x >= l.x0 && x <= l.x1 && z >= l.z0 && z <= l.z1 && h < l.level - 0.05) return WATER_LAKE;
  return WATER_NONE;
}

// ── river bends (R10: gravel bars on the inside of a bend) ─────────────────
// A documented copy of shared/terrain.ts's segment columns (the table is
// exported read-only; the harness checks this reading against riverWaterAt).
const C_AX = 0, C_AZ = 1, C_BX = 2, C_BZ = 3, C_DA = 6, C_DB = 7;
export const RIVER_COLS_MIRROR = 15;
const RIVER_CELL_MIRROR = 128;
const cellKey = (ix: number, iz: number): number => (ix + 32768) * 65536 + (iz + 32768);

/**
 * Inner-bend strength at (x, z): the nearest covering-or-bank segment's
 * river turns toward the point's side ⇒ sin(turn) (0..1); the outside of a
 * bend, a straight reach or no river ⇒ 0. Depositional point bars form on the
 * inside of meanders, where the flow slows.
 */
export function riverBendAt(seed: string, x: number, z: number): number {
  const net = riverNetworkTable(seed);
  if (!net) return 0;
  const key = cellKey(Math.floor(x / RIVER_CELL_MIRROR), Math.floor(z / RIVER_CELL_MIRROR));
  let lo = 0, hi = net.cellKeys.length - 1, at = -1;
  while (lo <= hi) { const mid = (lo + hi) >> 1; const k = net.cellKeys[mid]; if (k === key) { at = mid; break; } if (k < key) lo = mid + 1; else hi = mid - 1; }
  if (at < 0) return 0;
  const seg = net.seg, C = RIVER_COLS_MIRROR;
  let best = -1, bestD = Infinity, bestSide = 0;
  for (let e = net.cellStart[at]; e < net.cellStart[at + 1]; e++) {
    const s = net.cellSegs[e], o = s * C;
    const ax = seg[o + C_AX], az = seg[o + C_AZ], dx = seg[o + C_BX] - ax, dz = seg[o + C_BZ] - az;
    const L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
    const ex = x - ax - dx * t, ez = z - az - dz * t;
    const d = Math.hypot(ex, ez);
    const hw = (seg[o + C_DA] + (seg[o + C_DB] - seg[o + C_DA]) * t) * 0.5;
    if (d > hw + 24 || d >= bestD) continue;
    bestD = d; best = s; bestSide = dx * (z - az) - dz * (x - ax);
  }
  if (best < 0) return 0;
  // Neighbours in the SAME river: consecutive segments share an endpoint exactly (one decoded point).
  const o = best * C;
  const dirX = seg[o + C_BX] - seg[o + C_AX], dirZ = seg[o + C_BZ] - seg[o + C_AZ];
  let px = dirX, pz = dirZ, nx = dirX, nz = dirZ;
  if (best > 0) { const p = (best - 1) * C; if (seg[p + C_BX] === seg[o + C_AX] && seg[p + C_BZ] === seg[o + C_AZ]) { px = seg[p + C_BX] - seg[p + C_AX]; pz = seg[p + C_BZ] - seg[p + C_AZ]; } }
  if (best < net.count - 1) { const q = (best + 1) * C; if (seg[q + C_AX] === seg[o + C_BX] && seg[q + C_AZ] === seg[o + C_BZ]) { nx = seg[q + C_BX] - seg[q + C_AX]; nz = seg[q + C_BZ] - seg[q + C_AZ]; } }
  const lp = Math.hypot(px, pz) || 1, ln = Math.hypot(nx, nz) || 1;
  const turn = (px * nz - pz * nx) / (lp * ln); // sin of the turn (+ = toward the left of travel)
  if (Math.abs(turn) < 0.08 || Math.sign(turn) !== Math.sign(bestSide)) return 0;
  return Math.min(1, Math.abs(turn) * 2.2);
}

// ── routes (R11: the across-track coordinate the ruts are drawn on) ────────
interface RouteBins { segs: RouteSeg[]; bins: Map<number, number[]> }
const ROUTE_BIN = 256;
const routeBinCache = new Map<string, RouteBins>();
function routeBins(seed: string): RouteBins {
  let rb = routeBinCache.get(seed);
  if (rb) return rb;
  const segs = routeSegments(seed);
  const bins = new Map<number, number[]>();
  const reach = ROUTE_NONE + 2;
  segs.forEach((sg, i) => {
    const x0 = Math.floor((Math.min(sg.ax, sg.bx) - reach) / ROUTE_BIN), x1 = Math.floor((Math.max(sg.ax, sg.bx) + reach) / ROUTE_BIN);
    const z0 = Math.floor((Math.min(sg.az, sg.bz) - reach) / ROUTE_BIN), z1 = Math.floor((Math.max(sg.az, sg.bz) + reach) / ROUTE_BIN);
    for (let bx = x0; bx <= x1; bx++) for (let bz = z0; bz <= z1; bz++) {
      // Only the bins the segment actually passes near (a long diagonal would otherwise fill its whole box).
      const cx = (bx + 0.5) * ROUTE_BIN, cz = (bz + 0.5) * ROUTE_BIN;
      if (segDist(sg, cx, cz) > ROUTE_BIN * 0.75 + reach) continue;
      const k = cellKey(bx, bz); const a = bins.get(k); if (a) a.push(i); else bins.set(k, [i]);
    }
  });
  rb = { segs, bins };
  routeBinCache.set(seed, rb);
  return rb;
}
function segDist(sg: RouteSeg, x: number, z: number): number {
  const dx = sg.bx - sg.ax, dz = sg.bz - sg.az, L2 = dx * dx + dz * dz;
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((x - sg.ax) * dx + (z - sg.az) * dz) / L2)) : 0;
  return Math.hypot(x - sg.ax - dx * t, z - sg.az - dz * t);
}
/**
 * SIGNED distance (m) across the nearest regional route's centreline — the
 * sign is the side of travel (a → b), so the value is LINEAR across a
 * straight track and interpolates exactly between vertices 5 m apart: the
 * fragment shader draws 30 cm ruts from it. ROUTE_NONE past reach.
 */
export function routeAcross(seed: string, x: number, z: number): number {
  const rb = routeBins(seed);
  const ids = rb.bins.get(cellKey(Math.floor(x / ROUTE_BIN), Math.floor(z / ROUTE_BIN)));
  if (!ids) return ROUTE_NONE;
  let best = ROUTE_NONE, bestAbs = ROUTE_NONE;
  for (const i of ids) {
    const sg = rb.segs[i];
    const dx = sg.bx - sg.ax, dz = sg.bz - sg.az, L = Math.hypot(dx, dz);
    if (!(L > 0)) continue;
    const t = ((x - sg.ax) * dx + (z - sg.az) * dz) / (L * L);
    if (t < -0.02 || t > 1.02) continue; // past an end: the neighbour segment owns the point
    const across = (dx * (z - sg.az) - dz * (x - sg.ax)) / L;
    if (Math.abs(across) < bestAbs) { bestAbs = Math.abs(across); best = across; }
  }
  return bestAbs < ROUTE_NONE ? best : ROUTE_NONE;
}

/** The nearest point on any regional route (probe lever; linear scan — not for per-frame use). */
export function nearestRoutePoint(seed: string, x: number, z: number): { x: number; z: number; d: number; dirX: number; dirZ: number } | null {
  let best: { x: number; z: number; d: number; dirX: number; dirZ: number } | null = null;
  for (const sg of routeSegments(seed)) {
    const dx = sg.bx - sg.ax, dz = sg.bz - sg.az, L2 = dx * dx + dz * dz; if (!(L2 > 0)) continue;
    const t = Math.max(0.05, Math.min(0.95, ((x - sg.ax) * dx + (z - sg.az) * dz) / L2));
    const px = sg.ax + dx * t, pz = sg.az + dz * t, d = Math.hypot(px - x, pz - z);
    if (!best || d < best.d) { const L = Math.sqrt(L2); best = { x: px, z: pz, d, dirX: dx / L, dirZ: dz / L }; }
  }
  return best;
}

// ── terraces (the strata band weight) ──────────────────────────────────────
/**
 * The terrace layer's blend weight 0..1 at (x, z) — READ back out of the
 * exported `terraceHeight` (shared/terrain.ts is not ours to change): at
 * h₁ = ⌊h/14⌋·14 + 0.5 the quantised profile sits 0.5 m UNDER h₁, so
 * terraceHeight(h₁) − h₁ = −0.5·k and k = the blend (≤ TERRACE_MAX 0.85).
 */
export const TERRACE_STEP_MIRROR = 14;
export function terraceWeight(seed: string, x: number, z: number, h: number): number {
  const h1 = Math.floor(h / TERRACE_STEP_MIRROR) * TERRACE_STEP_MIRROR + 0.5;
  if (h1 <= 140) return 0;
  const k = -(terraceHeight(seed, x, z, h1) - h1) / 0.5;
  return Math.max(0, Math.min(1, k / 0.85));
}

// ── the chunk ground buffer ────────────────────────────────────────────────
function landformGrid(seed: string, g: GridSpec, filler: FieldFiller | null, withWeights: boolean): { h: Float64Array; w: Float64Array | null } {
  const n = g.n, N = n * n;
  const h = new Float64Array(N);
  const w = withWeights ? new Float64Array(N * 6) : null;
  const size = (n - 1) * g.step, cx = g.x0 + size / 2, cz = g.z0 + size / 2;
  const f = filler?.fillChunk(seed, cx, cz, size, n - 1) ?? null;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    const i = r * n + c;
    const x = g.x0 + c * g.step, z = g.z0 + r * g.step;
    if (f) {
      h[i] = landformHeight(seed, x, z, f.heights[i]);
      if (w) for (let k = 0; k < 6; k++) w[i * 6 + k] = f.weights[i * 6 + k];
    } else {
      h[i] = terrainHeight(seed, x, z);
      if (w) { const bw = biomeWeights(seed, x, z); for (let k = 0; k < 6; k++) w[i * 6 + k] = bw[BIOMES[k]]; }
    }
  }
  return { h, w };
}

/**
 * Rule fields by chunk: the field reads the ANALYTIC landform on fixed world
 * grids (no stamps, no edits, no seg), so a chunk's re-LOD — which walking does
 * constantly — reuses it. ~1,570 landform samples a chunk is the rules' whole cost.
 */
const FIELD_CACHE_MAX = 64;
const fieldCache = new Map<string, RuleField>();
export const ruleFieldStats = { hits: 0, misses: 0 };

/** The rule field of one chunk (fine 8 m / coarse 64 m / forest 32 m grids + water, bends). */
export function chunkRuleField(seed: string, sea: number, minX: number, minZ: number, size: number, filler: FieldFiller | null, fineStep = FINE_STEP): RuleField {
  const key = `${seed}|${minX}|${minZ}|${size}|${sea}|${fineStep}`;
  const hit = fieldCache.get(key);
  if (hit) { fieldCache.delete(key); fieldCache.set(key, hit); ruleFieldStats.hits++; return hit; }
  ruleFieldStats.misses++;
  const f = buildChunkRuleField(seed, sea, minX, minZ, size, filler, fineStep);
  fieldCache.set(key, f);
  if (fieldCache.size > FIELD_CACHE_MAX) fieldCache.delete(fieldCache.keys().next().value as string);
  return f;
}

function buildChunkRuleField(seed: string, sea: number, minX: number, minZ: number, size: number, filler: FieldFiller | null, fineStep: number): RuleField {
  const fine = gridFor(minX, minZ, size, fineStep, FINE_MARGIN);
  const coarse = gridFor(minX, minZ, size, COARSE_STEP, COARSE_MARGIN);
  const forest = gridFor(minX, minZ, size, FOREST_STEP, FOREST_MARGIN);
  const fh = landformGrid(seed, fine, filler, false).h;
  const ch = landformGrid(seed, coarse, filler, false).h;
  const fo = landformGrid(seed, forest, filler, true);
  const fd = new Float32Array(forest.n * forest.n);
  for (let r = 0; r < forest.n; r++) for (let c = 0; c < forest.n; c++) {
    const i = r * forest.n + c;
    let bi = 0, bw = -1;
    for (let k = 0; k < 6; k++) { const v = fo.w![i * 6 + k]; if (v > bw) { bw = v; bi = k; } }
    fd[i] = forestDensityMap(seed, forest.x0 + c * forest.step, forest.z0 + r * forest.step, sea, fo.h[i], BIOMES[bi]);
  }
  const reach = size / 2 + FINE_MARGIN * fineStep + 16;
  const lakes = bakedLakesNear(seed, minX + size / 2, minZ + size / 2, reach);
  return buildRuleField({
    minX, minZ, size, fine, fineH: fh, coarse, coarseH: ch, forest, forestD: fd,
    waterAt: (x, z, h) => waterClassAt(seed, sea, lakes, x, z, h),
    bendAt: (x, z) => riverBendAt(seed, x, z),
  });
}

/** Horizon chunks' fine grid (m) and the seg at or under which a chunk counts as horizon. */
export const HORIZON_FINE_STEP = 32;
export const HORIZON_SEG_MAX = 10;

export interface ChunkGroundInput {
  seed: string; sea: number;
  /** Chunk corner (min x / z) and side (m); the vertex grid is (seg+1)² row-major, x = minX + col/seg·size. */
  minX: number; minZ: number; size: number; seg: number;
  heights: ArrayLike<number>;
  /** Six climate weights per vertex (BIOMES order, painted). */
  weights: ArrayLike<number>;
  road: ArrayLike<number>; field: ArrayLike<number>;
}

/**
 * The interleaved ground buffer of a chunk: GROUND_STRIDE floats per vertex
 * (lib/ground_layers.ts). `filler` = the terrain kernel when installed.
 */
export function chunkGroundBuffer(inp: ChunkGroundInput, filler: FieldFiller | null): Float32Array {
  const side = inp.seg + 1, count = side * side;
  const out = new Float32Array(count * GROUND_STRIDE);
  // HORIZON chunks (seg ≤ 10, > 1.5 km out) read a 32 m fine grid: 169 samples instead of 1,369
  // (a horizon chunk renders 121 vertices — the 8 m field was 10× its own cost). The 8 m field
  // stays for seg ≥ 24, so the refine nearest the player (24 ↔ 48) is bit-identical.
  const field = chunkRuleField(inp.seed, inp.sea, inp.minX, inp.minZ, inp.size, filler, inp.seg <= HORIZON_SEG_MAX ? HORIZON_FINE_STEP : FINE_STEP);
  const ri = emptyRuleInput();
  const bw = new Float64Array(6);
  ri.biome = bw; ri.sea = inp.sea;
  for (let i = 0; i < count; i++) {
    const col = i % side, row = (i / side) | 0;
    const x = inp.minX + (col / inp.seg) * inp.size, z = inp.minZ + (row / inp.seg) * inp.size;
    sampleRuleField(field, x, z, ri);
    ri.h = inp.heights[i];
    for (let k = 0; k < 6; k++) bw[k] = inp.weights[i * 6 + k];
    ri.road = inp.road[i]; ri.field = inp.field[i];
    ri.routeD = inp.road[i] > 0.02 ? routeAcross(inp.seed, x, z) : ROUTE_NONE;
    ri.terrace = terraceWeight(inp.seed, x, z, ri.h);
    ri.n40 = ruleNoise(x, z, 40, 7) * 0.7 + ruleNoise(x, z, 13, 11) * 0.3;
    groundRule(ri, out, i * GROUND_STRIDE);
  }
  return out;
}

/** Sum of layer weights of a vertex (a harness check: always 1). */
export function weightSum(buf: ArrayLike<number>, o: number): number { let s = 0; for (let i = 0; i < GROUND_LAYER_COUNT; i++) s += buf[o + i]; return s; }
export type { Biome };
